import { createTheme, Theme } from "@mui/material/styles";
import type { PaletteMode } from "@mui/material";
import {
  darkTokens,
  fontFamily,
  lightTokens,
  radius,
  softShadow,
} from "./tokens";

// Build a full MUI theme for the requested colour mode.
export function getTheme(mode: PaletteMode): Theme {
  const isLight = mode === "light";
  const tokens = isLight ? lightTokens : darkTokens;

  return createTheme({
    palette: {
      mode,
      primary: tokens.primary,
      background: tokens.background,
      text: tokens.text,
      divider: tokens.divider,
      error: tokens.error,
    },
    shape: {
      borderRadius: radius.card,
    },
    typography: {
      fontFamily,
      h1: { fontSize: "2rem", fontWeight: 700, letterSpacing: "-0.02em" },
      h2: { fontSize: "1.75rem", fontWeight: 700, letterSpacing: "-0.02em" },
      h3: { fontSize: "1.5rem", fontWeight: 700, letterSpacing: "-0.01em" },
      h4: { fontSize: "1.375rem", fontWeight: 700, letterSpacing: "-0.01em" },
      h5: { fontSize: "1.2rem", fontWeight: 600 },
      h6: { fontSize: "1.05rem", fontWeight: 600 },
      subtitle1: { fontSize: "1rem", fontWeight: 600 },
      subtitle2: { fontSize: "0.9rem", fontWeight: 600 },
      body1: { fontSize: "1rem", lineHeight: 1.6 },
      body2: { fontSize: "0.9rem", lineHeight: 1.55 },
      button: { fontWeight: 600, letterSpacing: 0 },
      caption: { fontSize: "0.8125rem" },
    },
    components: {
      MuiButton: {
        defaultProps: {
          disableElevation: true,
        },
        styleOverrides: {
          root: {
            borderRadius: radius.button,
            textTransform: "none",
            paddingInline: "1.1rem",
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            // Remove MUI's dark-mode "elevation overlay" gradient for a flatter, cleaner look.
            backgroundImage: "none",
          },
          rounded: {
            borderRadius: radius.card,
          },
        },
      },
      MuiCard: {
        defaultProps: {
          elevation: 0,
        },
        styleOverrides: {
          root: {
            borderRadius: radius.card,
            border: `1px solid ${tokens.divider}`,
          },
        },
      },
      MuiAccordion: {
        defaultProps: {
          elevation: 0,
        },
        styleOverrides: {
          root: {
            border: `1px solid ${tokens.divider}`,
            borderRadius: radius.card,
            backgroundImage: "none",
            "&:before": { display: "none" },
            "&.Mui-expanded": { margin: 0 },
          },
        },
      },
      MuiBottomNavigation: {
        styleOverrides: {
          root: {
            backgroundColor: tokens.background.paper,
            borderTop: `1px solid ${tokens.divider}`,
          },
        },
      },
      MuiBottomNavigationAction: {
        styleOverrides: {
          root: {
            "&.Mui-selected": { color: tokens.primary.main },
          },
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: { borderRadius: radius.input },
        },
      },
      MuiDialog: {
        styleOverrides: {
          paper: { borderRadius: radius.card },
        },
      },
      MuiTooltip: {
        styleOverrides: {
          tooltip: { borderRadius: 8 },
        },
      },
    },
    shadows: buildShadows(isLight),
  });
}

// Replace MUI's default (heavy) shadow ramp with soft, low-opacity shadows
// for the low elevations we actually use, keeping the rest subtle.
function buildShadows(isLight: boolean): Theme["shadows"] {
  const base = createTheme().shadows.slice();
  const soft = isLight ? softShadow.light : softShadow.dark;
  base[1] = soft;
  base[2] = soft;
  base[3] = soft;
  return base as Theme["shadows"];
}
